import { Form, useFormContext } from "@/components/form";
import { CollapsibleSection } from "@/components/form/collapsible-section";
import { Stack, TextField, InputAdornment, IconButton, Alert } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { Dialog, ModalContextExtractor, ModalHeader, useOpenModal } from "@/components/modal";
import type { Cliente } from "@/shared/types/cliente";
import { NaturalezaReceptorEnum } from "@/shared/types/contribuyente";
import { getClienteDisplayValue } from "../../utils/de-data-utils";
import ClienteInfoCard from "../cliente-info-card";
import ClienteManualSearch from "@/features/facturacion-electronica/components/cliente-manual-search";

interface AutofacturaGeneralInfoFieldsProps {
    detalleFormRef?: React.MutableRefObject<any>;
    onClienteChange?: (clienteError: Cliente | null) => void;
    cliente?: Cliente | null;
    clienteError?: boolean;
    clienteIncompleto?: boolean;
}

export default function AutofacturaGeneralInfoFields({
    onClienteChange,
    cliente,
    clienteError = false,
    clienteIncompleto = false,
}: AutofacturaGeneralInfoFieldsProps) {
    const form = useFormContext();
    const openModal = useOpenModal();

    // Seleccion de vendedor desde el modal de busqueda manual
    const handleClienteSelect = (cliente: Cliente) => {
        if (cliente.id) {
            form.setFieldValue("vendedorId", cliente.id);
        } else {
            form.setFieldValue("vendedorId", "");
        }
        if (onClienteChange) {
            onClienteChange(cliente);
        }
    };

    const handleClearVendedor = () => {
        form.setFieldValue("vendedorId", "");
        if (onClienteChange) {
            onClienteChange(null);
        }
    };

    return (
        <CollapsibleSection title="Información General">
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems="flex-start" flexWrap="wrap">

                <Stack flex={{ xs: '1 1 100%', sm: '2 1 350px' }} minWidth="280px">
                    <TextField
                        label="Cliente *"
                        size="small"
                        fullWidth
                        value={getClienteDisplayValue(cliente)}
                        placeholder="Buscar cliente..."
                        error={clienteError && !cliente}
                        helperText={clienteError && !cliente ? "Cliente es requerido" : ""}
                        slotProps={{
                            input: {
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {cliente && (
                                            <IconButton size="small" onClick={handleClearVendedor}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        <IconButton size="small" onClick={() => openModal("searchCliente")}>
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }
                        }}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => !cliente && openModal("searchCliente")}
                    />
                    <ClienteInfoCard cliente={cliente || null} title="Cliente" />
                    {clienteIncompleto && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            El cliente debe tener ubicaciones configuradas para crear una autofactura.
                        </Alert>
                    )}
                    {cliente && cliente.naturaleza === NaturalezaReceptorEnum.Contribuyente && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            El cliente seleccionado es un contribuyente. Las autofacturas solo pueden ser emitidas a clientes no contribuyentes.
                        </Alert>
                    )}
                </Stack>

                <Stack flex={{ xs: '1 1 100%', sm: '1 1 250px' }} minWidth="200px">
                    <Form.TextField name="observaciones" label="Observaciones" size="small" fullWidth />
                </Stack>
            </Stack>

            <Dialog code="searchCliente" size="lg">
                <ModalHeader>Buscar Cliente</ModalHeader>
                <ModalContextExtractor>
                    {({ handleClose }) => (
                        <ClienteManualSearch
                            selectedClienteId={cliente?.id}
                            onClienteSelect={(cliente: Cliente) => {
                                handleClienteSelect(cliente);
                                handleClose();
                            }}
                        />
                    )}
                </ModalContextExtractor>
            </Dialog>
        </CollapsibleSection>
    );
}
